import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import "./TicketCaisse.css";
import jsPDF from "jspdf";
import "jspdf-autotable";

function TicketCaisseS({ currentUser }) {
  const [bills, setBills] = useState([]);
  const [products, setProducts] = useState([]);
  const [subProducts, setSubProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [showBillPopup, setShowBillPopup] = useState(false);
  const [billsCache, setBillsCache] = useState({});
  const [cacheTimestamp, setCacheTimestamp] = useState(null);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("/api/Locations/getAllLocations", {
          headers: { Authorised: token },
          timeout: 10000
        });
        setLocations(res.data || []);
      } catch (err) {
        console.log("⚠️ Erreur chargement locations :", err.message);
      }
    };

    loadLocations();
  }, [currentUser]);

  // Configuration des logos
  const getLocationConfig = () => {
    if (!currentUser?.localisation) {
      return {
        logo: "/spintoss.png",
        name: "SPINTOSS",
        address: ""
      };
    }

    const locationName = currentUser.localisation.name?.toLowerCase() || "";
    
    const configs = {
      "jm": {
        logo: "/spintoss.png",
        name: "SPINTOSS JM",
        address: "JARDINS D'EL MENZAH 2"
      },
      "tm": {
        logo: "/spintoss.png", 
        name: "SPINTOSS TM",
        address: "TUNISIA MALL"
      },
      "default": {
        logo: "/spintoss.png",
        name: "SPINTOSS",
        address: ""
      }
    };

    if (locationName.includes("jm") || locationName.includes("menzah")) {
      return configs.jm;
    }
    
    if (locationName.includes("tm") || locationName.includes("tunisia") || locationName.includes("mall")) {
      return configs.tm;
    }

    return configs.default;
  };

  // 🔍 Fonction pour extraire l'ID de localisation quelle que soit sa forme
  const extractLocId = (loc) => {
    if (!loc) return null;

    // Cas string direct
    if (typeof loc === "string") return loc;

    // Cas {_id: "..."}
    if (typeof loc._id === "string") return loc._id;

    // Cas {"$oid": "..."}
    if (typeof loc.$oid === "string") return loc.$oid;

    // Cas {_id: {"$oid": "..."}}
    if (loc._id && typeof loc._id.$oid === "string") return loc._id.$oid;

    // Cas MongoDB Object → try toString()
    if (loc.toString) return loc.toString();

    return null;
  };

  // Fonction pour obtenir la localisation de l'utilisateur
  const getUserLocalisationId = useCallback(() => {
    console.log("🔍 Analyse de currentUser:", currentUser);
    
    // 1. Essayer depuis currentUser.localisation
    if (currentUser?.localisation) {
      const locId = extractLocId(currentUser.localisation);
      if (locId) {
        console.log("📍 Localisation trouvée dans currentUser:", locId);
        return locId;
      }
    }
    
    // 2. Essayer depuis localStorage
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.localisation) {
          const locId = extractLocId(parsedUser.localisation);
          if (locId) {
            console.log("📍 Localisation trouvée dans localStorage:", locId);
            return locId;
          }
        }
      }
    } catch (e) {
      console.warn("⚠️ Erreur parsing localStorage:", e);
    }
    
    // 3. Si admin, retourner 'admin'
    if (currentUser?.role === 'admin') {
      console.log("📍 Utilisateur admin - pas de filtre");
      return 'admin';
    }
    
    console.log("📍 Aucune localisation trouvée");
    return null;
  }, [currentUser, locations]);

  // Fonction pour charger les employés
  const loadEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/Employees/", {
        headers: { Authorised: token },
        timeout: 10000
      });
      setEmployees(res.data || []);
      console.log("✅ Employés chargés:", res.data?.length || 0);
    } catch (err) {
      console.warn("⚠️ Erreur chargement employés:", err.message);
      setEmployees([]);
    }
  }, []);

  // Fonction optimisée pour charger les données
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      console.log("🔄 Chargement des données...");

      // Charger les produits
      const productsRes = await axios.get("/api/Product/getProduct", { 
        headers: { Authorised: token },
        timeout: 15000
      });
      setProducts(productsRes.data || []);
      console.log("✅ Produits chargés:", productsRes.data?.length || 0);

      // Charger les factures
      console.log("📥 Chargement des factures...");
      const billsRes = await axios.get("/api/Bills/getAllBills", {
        headers: { Authorised: token },
        timeout: 30000,
        params: {
          limit: 1000,
          sort: "-date"
        }
      });
      
      // Filtrer les factures combinées partielles
      const filteredBills = (billsRes.data || []).filter(bill => {
        // Exclure les factures combinées partielles (avec combinedPaymentIndex > 1)
        if (bill.combinedPayment && bill.combinedPaymentIndex && bill.combinedPaymentIndex > 1) {
          console.log(`⚠️ Exclu facture combinée partielle: ${bill.billNumber}`);
          return false;
        }
        return true;
      });
      
      setBills(filteredBills);
      console.log("✅ Factures chargées:", filteredBills.length, "(filtrées sur", billsRes.data?.length || 0, "total)");

      // Charger les sous-produits - GESTION DES ERREURS 404
      try {
        const subProductsRes = await axios.get("/api/SubProduct/getAllSubProducts", {
          headers: { Authorised: token },
          timeout: 5000
        });
        setSubProducts(subProductsRes.data || []);
        console.log("✅ Sous-produits chargés:", subProductsRes.data?.length || 0);
      } catch (subErr) {
        console.warn("⚠️ Sous-produits non chargés:", subErr.message);
        
        // Essayez une route sans 's'
        try {
          const subProductsRes2 = await axios.get("/api/SubProduct/getAllSubProduct", {
            headers: { Authorised: token },
            timeout: 5000
          });
          setSubProducts(subProductsRes2.data || []);
          console.log("✅ Sous-produits chargés via route alternative:", subProductsRes2.data?.length || 0);
        } catch (subErr2) {
          console.warn("⚠️ Route alternative échouée:", subErr2.message);
          setSubProducts([]);
        }
      }

      // Charger les employés
      await loadEmployees();

    } catch (err) {
      console.error("❌ Erreur chargement données :", err.message || err);
      
      if (err.code === 'ECONNABORTED') {
        console.error("⏱️ Timeout de la requête - Réessayez");
      } else if (err.response) {
        console.error(`📡 Erreur serveur: ${err.response.status}`, err.response.data);
      }
    } finally {
      setLoading(false);
    }
  }, [loadEmployees]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Optimisation des maps produits/sous-produits
  const { productById, subById } = useMemo(() => {
    const pMap = new Map();
    const sMap = new Map();

    (products || []).forEach((p) => {
      if (p && p._id) {
        pMap.set(extractLocId(p._id), p);
      }

      if (Array.isArray(p.subProducts)) {
        p.subProducts.forEach((sp) => {
          if (sp && sp._id) {
            sMap.set(extractLocId(sp._id), { ...sp, parentProduct: p });
          }
        });
      }

      if (Array.isArray(p.subProductsV)) {
        p.subProductsV.forEach((sp) => {
          if (sp && sp._id) {
            sMap.set(extractLocId(sp._id), { ...sp, parentProduct: p });
          }
        });
      }
    });

    (subProducts || []).forEach((sp) => {
      if (sp && sp._id) {
        sMap.set(extractLocId(sp._id), sp);
      }
    });

    return { productById: pMap, subById: sMap };
  }, [products, subProducts]);

  // Fonction pour extraire l'ID d'un item
  const getItemIdString = (item) => {
    if (!item) return null;
    
    const candidates = [
      item.id,
      item._id,
      item.productId,
      item.subProductId,
      item.product
    ];
    
    for (const cand of candidates) {
      if (cand) {
        try {
          const str = extractLocId(cand);
          if (str) {
            return str;
          }
        } catch (e) {
          continue;
        }
      }
    }
    
    return null;
  };

  // Fonction pour obtenir les détails d'un produit - PRIX ORIGINAL (sans remise)
  const getProductDetails = useCallback((item) => {
    if (!item) return { 
      name: "Produit inconnu", 
      parent: null, 
      price: 0, 
      originalPrice: 0,
      localisation: null, 
      hasSubProduct: false,
      category: null,
      productData: null
    };

    const idStr = getItemIdString(item);
    
    if (idStr && productById.has(idStr)) {
      const mainProduct = productById.get(idStr);
      const price = typeof item.price === "number" ? item.price : (mainProduct.price || 0);
      return {
        parent: mainProduct.name || null,
        name: null,
        price: price, // Prix original
        originalPrice: price, // Même prix (pas encore de remise)
        localisation: extractLocId(mainProduct.localisation),
        hasSubProduct: false,
        category: mainProduct.category || "Non catégorisé",
        productData: mainProduct
      };
    }

    if (idStr && subById.has(idStr)) {
      const sub = subById.get(idStr);
      const parentProduct = sub.parentProduct || (sub.product ? productById.get(extractLocId(sub.product)) : null);
      const price = typeof item.price === "number" ? item.price : (sub.price || 0);
      
      return {
        parent: parentProduct?.name || null,
        name: sub.name || item.name || null,
        price: price, // Prix original
        originalPrice: price, // Même prix (pas encore de remise)
        localisation: extractLocId(sub.localisation || parentProduct?.localisation),
        hasSubProduct: true,
        category: parentProduct?.category || sub.category || "Non catégorisé",
        productData: sub
      };
    }

    const price = item.price || 0;
    return {
      name: item.name || "Produit inconnu",
      parent: null,
      price: price, // Prix original
      originalPrice: price, // Même prix
      localisation: null,
      hasSubProduct: false,
      category: item.category || "Non catégorisé",
      productData: item
    };
  }, [productById, subById]);

  // NOUVELLES FONCTIONS : Calculs avec remise
  const calculatePriceWithDiscount = (priceTTC, billDiscount = 0, isStaffAddition = false) => {
    let finalPrice = priceTTC;
    
    // Appliquer la remise si elle existe (remise standard)
    if (billDiscount > 0) {
      finalPrice = priceTTC - (priceTTC * billDiscount / 100);
    }
    
    // Si c'est une addition personnel, appliquer 20% (cela remplace la remise standard)
    if (isStaffAddition) {
      finalPrice = priceTTC - (priceTTC * 0.20);
    }
    
    return finalPrice;
  };

  const calculateHT = (priceTTC) => {
    return priceTTC / 1.07;
  };

  const calculateTVA = (priceTTC) => {
    return priceTTC - (priceTTC / 1.07);
  };

  // Fonction pour obtenir le nom de l'employé à partir de l'ID - VERSION ROBUSTE
  const getEmployeeInfo = useCallback((employeeId) => {
    if (!employeeId || !Array.isArray(employees) || employees.length === 0) {
      return null;
    }
    
    // Extraire l'ID de l'employé de différentes manières
    let extractedId;
    
    // Si c'est un objet avec $oid
    if (employeeId && typeof employeeId === 'object' && employeeId.$oid) {
      extractedId = employeeId.$oid;
    }
    // Si c'est un objet avec _id
    else if (employeeId && typeof employeeId === 'object' && employeeId._id) {
      extractedId = extractLocId(employeeId._id);
    }
    // Si c'est une chaîne
    else if (typeof employeeId === 'string') {
      extractedId = employeeId;
    }
    // Sinon, essayer d'extraire
    else {
      extractedId = extractLocId(employeeId);
    }
    
    if (!extractedId) {
      return null;
    }
    
    // Normaliser l'ID (enlever les espaces, etc.)
    const normalizedId = extractedId.trim().toLowerCase();
    
    // Chercher l'employé par _id avec différentes méthodes
    let employee = null;
    
    // 1. Chercher par _id exact
    employee = employees.find(emp => {
      const empId = extractLocId(emp._id);
      return empId && empId.toLowerCase() === normalizedId;
    });
    
    // 2. Si non trouvé, chercher par _id.$oid
    if (!employee) {
      employee = employees.find(emp => {
        if (emp._id && emp._id.$oid) {
          return emp._id.$oid.toLowerCase() === normalizedId;
        }
        return false;
      });
    }
    
    // 3. Si non trouvé, chercher par matricule
    if (!employee) {
      employee = employees.find(emp => {
        if (emp.matricule) {
          return emp.matricule.toLowerCase() === normalizedId;
        }
        return false;
      });
    }
    
    if (employee) {
      return {
        name: employee.name || "Inconnu",
        matricule: employee.matricule || "N/A",
        id: extractedId,
        department: employee.department || "",
        position: employee.position || ""
      };
    }
    
    return null;
  }, [employees]);

  // Fonction pour obtenir les informations du personnel
  const getStaffInfo = useCallback((bill) => {
    if (!bill?.isStaffAddition) return null;
    
    const staffMember = bill?.staffMember;
    if (!staffMember) return null;
    
    // Obtenir les infos de l'employé via son ID
    const employeeInfo = getEmployeeInfo(staffMember);
    if (employeeInfo) {
      return {
        name: employeeInfo.name,
        matricule: employeeInfo.matricule,
        id: employeeInfo.id,
        department: employeeInfo.department,
        position: employeeInfo.position,
        role: "Employé"
      };
    }
    
    return null;
  }, [getEmployeeInfo]);

  // Fonction pour obtenir les détails d'un produit AVEC REMISE APPLIQUÉE
  const getProductDetailsWithDiscount = useCallback((item, bill) => {
    if (!item) return { 
      name: "Produit inconnu", 
      price: 0,
      originalPrice: 0,
      discountApplied: 0,
      isStaffAddition: false,
      staffMember: null,
      staffName: null,
      staffMatricule: null,
      hasDiscount: false,
      isCombinedPayment: false,
      paymentBreakdown: null
    };

    const basicDetails = getProductDetails(item);
    
    // Récupérer les informations de remise de la facture
    const billDiscount = bill?.discount || 0;
    const isStaffAddition = bill?.isStaffAddition || false;
    const staffMember = bill?.staffMember || null;
    const isCombinedPayment = bill?.combinedPayment || false;
    
    // Obtenir les informations du personnel via getStaffInfo
    const staffInfo = getStaffInfo(bill);
    
    // Calculer le prix après remise
    let finalPrice = basicDetails.price;
    let discountApplied = 0;
    let hasDiscount = false;
    
    if (isStaffAddition) {
      // Addition personnel : 20% de remise
      discountApplied = 20;
      finalPrice = calculatePriceWithDiscount(basicDetails.price, 0, true);
      hasDiscount = true;
    } else if (billDiscount > 0) {
      // Remise standard
      discountApplied = billDiscount;
      finalPrice = calculatePriceWithDiscount(basicDetails.price, billDiscount, false);
      hasDiscount = true;
    }
    
    return {
      ...basicDetails,
      price: finalPrice,
      originalPrice: basicDetails.price,
      discountApplied,
      isStaffAddition,
      staffMember,
      staffName: staffInfo?.name || null,
      staffMatricule: staffInfo?.matricule || null,
      hasDiscount,
      isCombinedPayment,
      paymentBreakdown: null // Pour les factures combinées, on utilise combinedPaymentsDetail
    };
  }, [getProductDetails, calculatePriceWithDiscount, getStaffInfo]);

  // 🔄 FONCTION POUR DÉTECTER LES PAIEMENTS COMBINÉS - VERSION SIMPLIFIÉE
  const isCombinedPaymentBill = (bill) => {
    if (!bill) return false;
    
    // 1. Vérifier les champs explicites
    if (bill.combinedPayment === true) {
      return true;
    }
    
    // 2. Vérifier si c'est une facture combinée principale (index 1)
    if (bill.combinedPaymentIndex === 1 && bill.combinedPaymentTotal && bill.combinedPaymentTotal > 1) {
      return true;
    }
    
    return false;
  };

  // 🔄 FONCTION POUR REGROUPER LES FACTURES COMBINÉES - VERSION CORRIGÉE
  const groupCombinedPayments = (bills) => {
    console.log("🔄 Début du regroupement des paiements combinés");
    
    // Map pour regrouper les factures combinées par groupe
    const combinedGroups = new Map();
    const standaloneBills = [];
    const processedBills = new Set();
    
    // 1. Identifier les factures combinées principales
    bills.forEach(bill => {
      const billId = bill._id;
      if (processedBills.has(billId)) return;
      
      // Vérifier si c'est une facture combinée principale
      if (isCombinedPaymentBill(bill)) {
        console.log(`✅ Facture ${bill.billNumber} détectée comme paiement combiné principal`);
        
        // Créer un groupe pour cette facture combinée
        const groupKey = `combined_${bill.billNumber}_${bill.tableNumber || ''}_${new Date(bill.date).getTime()}`;
        
        if (!combinedGroups.has(groupKey)) {
          combinedGroups.set(groupKey, {
            mainBill: bill,
            paymentParts: [bill],
            totalTTC: bill.total || 0,
            paymentMethods: new Map(),
            combinedPaymentsDetail: bill.combinedPaymentsDetail || []
          });
        }
        
        processedBills.add(billId);
      } else {
        standaloneBills.push(bill);
        processedBills.add(billId);
      }
    });
    
    // 2. Pour chaque groupe, calculer la répartition des paiements
    const unifiedBills = [];
    
    combinedGroups.forEach((group, groupKey) => {
      const mainBill = group.mainBill;
      
      // Créer une facture unifiée avec les détails de paiement combiné
      const unifiedBill = {
        ...mainBill,
        _id: `combined_${mainBill._id}`,
        unifiedCombinedBill: true,
        paymentMethods: Array.from(group.paymentMethods.entries()),
        combinedPaymentsDetail: group.combinedPaymentsDetail,
        combinedPaymentParts: group.paymentParts.length,
        paymentMethod: "Combiné" // Forcer l'affichage "Combiné"
      };
      
      unifiedBills.push(unifiedBill);
    });
    
    console.log(`🔄 Résultat: ${unifiedBills.length} factures combinées unifiées, ${standaloneBills.length} factures normales`);
    
    return [...unifiedBills, ...standaloneBills];
  };

  // 🔥 FONCTION CORRIGÉE: Afficher la répartition des paiements combinés
  const renderCombinedPaymentBreakdown = (bill) => {
    if (!isCombinedPaymentBill(bill)) return null;
    
    // Utiliser combinedPaymentsDetail depuis la base de données
    if (!bill.combinedPaymentsDetail || bill.combinedPaymentsDetail.length === 0) return null;
    
    // Calculer les totaux par méthode de paiement
    const breakdownByMethod = {};
    let totalPaid = 0;
    
    bill.combinedPaymentsDetail.forEach(payment => {
      const method = payment.method;
      if (!breakdownByMethod[method]) {
        breakdownByMethod[method] = {
          amount: 0,
          count: 0
        };
      }
      breakdownByMethod[method].amount += payment.amount;
      breakdownByMethod[method].count += 1;
      totalPaid += payment.amount;
    });
    
    return (
      <div className="combined-payment-breakdown" style={{
        backgroundColor: '#e3f2fd',
        padding: '10px',
        borderRadius: '5px',
        margin: '10px 0',
        border: '1px solid #90caf9'
      }}>
        <h4 style={{color: '#1565c0', marginBottom: '10px'}}>🔄 DÉTAIL DU PAIEMENT COMBINÉ</h4>
        
        <table style={{width: '100%', fontSize: '12px'}}>
          <thead>
            <tr>
              <th style={{textAlign: 'left', padding: '5px'}}>Méthode</th>
              <th style={{textAlign: 'right', padding: '5px'}}>Montant</th>
              <th style={{textAlign: 'center', padding: '5px'}}>Nombre</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(breakdownByMethod).map(([method, data], index) => (
              <tr key={index} style={{borderBottom: '1px solid #bbdefb'}}>
                <td style={{padding: '5px'}}>{method}</td>
                <td style={{textAlign: 'right', padding: '5px', fontWeight: 'bold'}}>
                  {data.amount.toFixed(2)} DT
                </td>
                <td style={{textAlign: 'center', padding: '5px'}}>
                  {data.count} paiement{data.count !== 1 ? 's' : ''}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{fontWeight: 'bold', borderTop: '2px solid #1976d2'}}>
              <td style={{padding: '5px'}}>TOTAL</td>
              <td style={{textAlign: 'right', padding: '5px'}}>
                {totalPaid.toFixed(2)} DT
              </td>
              <td style={{textAlign: 'center', padding: '5px'}}>
                {bill.combinedPaymentsDetail.length}
              </td>
            </tr>
          </tfoot>
        </table>
        
        {/* Afficher le détail complet */}
        {bill.combinedPaymentsDetail && bill.combinedPaymentsDetail.length > 0 && (
          <div style={{marginTop: '10px', fontSize: '11px'}}>
            <div style={{fontWeight: 'bold', color: '#1565c0', marginBottom: '5px'}}>
              Détail par transaction:
            </div>
            {bill.combinedPaymentsDetail.map((payment, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                margin: '3px 0',
                padding: '3px 5px',
                backgroundColor: idx % 2 === 0 ? '#f5faff' : 'transparent',
                borderRadius: '3px'
              }}>
                <span>{payment.method}:</span>
                <span style={{fontWeight: 'bold'}}>{payment.amount.toFixed(2)} DT</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Gestion du popup facture
  const showBillDetails = (bill) => {
    setSelectedBill(bill);
    setShowBillPopup(true);
  };

  const closeBillPopup = () => {
    setShowBillPopup(false);
    setSelectedBill(null);
  };
  

  // 🔥 FONCTION CORRIGÉE: Imprimer la facture avec détails combinés
  const printBill = () => {
    if (!selectedBill) return;
    
    const locationConfig = getLocationConfig();
    const currentDate = new Date().toLocaleString();
    const billDate = new Date(selectedBill.date).toLocaleString();
    const staffInfo = getStaffInfo(selectedBill);
    
    // Calcul des totaux AVEC REMISE
    let totalOriginalTTC = 0;
    let totalTTC = 0;
    let totalHT = 0;
    let totalTVA = 0;
    let totalDiscountAmount = 0;
    
    const itemsHtml = selectedBill.items.map((item, index) => {
      const details = getProductDetailsWithDiscount(item, selectedBill);
      const itemOriginalTTC = details.originalPrice * (item.quantity || 1);
      const itemTTC = details.price * (item.quantity || 1);
      const itemHT = calculateHT(itemTTC);
      const itemTVA = calculateTVA(itemTTC);
      const itemDiscount = itemOriginalTTC - itemTTC;
      
      totalOriginalTTC += itemOriginalTTC;
      totalTTC += itemTTC;
      totalHT += itemHT;
      totalTVA += itemTVA;
      totalDiscountAmount += itemDiscount;
      
      // 🔥 AJOUT: Calcul du prix global (quantité * prix unitaire)
      const globalPrice = details.price * (item.quantity || 1);
      const originalGlobalPrice = details.originalPrice * (item.quantity || 1);
      
      let itemHtml = `
        <div style="display:flex; justify-content:space-between; margin:5px 0; font-size:11px;">
          <span>${details.name || details.parent || item.name || "Produit"} x${item.quantity || 1}</span>
          <span>
            <div>${globalPrice.toFixed(2)} DT TTC</div>
            ${originalGlobalPrice !== globalPrice ? `
              <div style="font-size:10px; color:#666; text-decoration:line-through;">
                ${originalGlobalPrice.toFixed(2)} DT
              </div>
            ` : ''}
          </span>
        </div>
      `;
      
      // Afficher l'information de remise si applicable
      if (details.hasDiscount) {
        itemHtml += `
          <div style="font-size:10px; color:#666; padding-left:15px;">
            (HT: ${itemHT.toFixed(2)} DT + TVA: ${itemTVA.toFixed(2)} DT)
          </div>
          <div style="font-size:10px; color:#d32f2f; padding-left:15px;">
            ⭐ Remise ${details.discountApplied}% appliquée
            ${details.isStaffAddition ? ' (Addition Personnel)' : ''}
          </div>
        `;
      } else {
        itemHtml += `
          <div style="font-size:10px; color:#666; padding-left:15px;">
            (HT: ${itemHT.toFixed(2)} DT + TVA: ${itemTVA.toFixed(2)} DT)
          </div>
        `;
      }
      
      return itemHtml;
    }).join('');

    // Section informations du personnel
    let staffSection = '';
    if (staffInfo) {
      staffSection = `
        <div style="margin:8px 0; padding:8px; background:#fff3cd; border-radius:5px; border:1px solid #ffeaa7;">
          <div style="font-weight:bold; color:#856404; font-size:11px;">INFORMATIONS PERSONNEL</div>
          <div style="display:flex; justify-content:space-between; margin:3px 0; font-size:10px;">
            <span>Nom:</span>
            <span>${staffInfo.name}</span>
          </div>
          <div style="display:flex; justify-content:space-between; margin:3px 0; font-size:10px;">
            <span>Matricule:</span>
            <span>${staffInfo.matricule}</span>
          </div>
          ${staffInfo.department ? `
            <div style="display:flex; justify-content:space-between; margin:3px 0; font-size:10px;">
              <span>Département:</span>
              <span>${staffInfo.department}</span>
            </div>
          ` : ''}
          <div style="display:flex; justify-content:space-between; margin:3px 0; font-size:10px;">
            <span>Type:</span>
            <span>Addition Personnel -20%</span>
          </div>
        </div>
      `;
    }

    // 🔥 SECTION MODIFIÉE: Paiement combiné avec répartition détaillée
    let combinedPaymentDetails = '';
    if (selectedBill.combinedPayment && selectedBill.combinedPaymentsDetail) {
      // Calculer le total payé
      const totalPaid = selectedBill.combinedPaymentsDetail.reduce((sum, payment) => sum + payment.amount, 0);
      
      combinedPaymentDetails = `
        <div style="margin:8px 0; padding:8px; background:#e3f2fd; border-radius:5px; border:1px solid #90caf9;">
          <div style="font-weight:bold; color:#1565c0; font-size:11px; margin-bottom:5px;">🔄 DÉTAIL DU PAIEMENT COMBINÉ</div>
          ${selectedBill.combinedPaymentsDetail.map((payment, index) => `
            <div style="display:flex; justify-content:space-between; font-size:10px; margin:3px 0;">
              <span>${payment.method}:</span>
              <span style="font-weight:bold;">${payment.amount.toFixed(2)} DT</span>
            </div>
          `).join('')}
          <div style="display:flex; justify-content:space-between; font-weight:bold; margin-top:5px; border-top:1px solid #1976d2; padding-top:5px;">
            <span>TOTAL PAYÉ:</span>
            <span>${totalPaid.toFixed(2)} DT</span>
          </div>
          ${Math.abs(totalPaid - selectedBill.total) > 0.01 ? `
            <div style="font-size:9px; color:#d32f2f; margin-top:3px;">
              Note: Différence de ${Math.abs(totalPaid - selectedBill.total).toFixed(2)} DT avec le total de la facture
            </div>
          ` : ''}
        </div>
      `;
    }

    const printableContent = `
      <div style="font-family:'Courier New', monospace; text-align:center; max-width:300px; margin:0 auto; font-size:12px;">
        <div style="text-align:center; margin-bottom:10px;">
          <img src="${locationConfig.logo}" alt="Logo" style="max-width:120px; max-height:40px;" onerror="this.style.display='none'" />
        </div>
        <h1 style="margin:0; font-size:16px;">${locationConfig.name}</h1>
        ${locationConfig.address ? `<p style="margin:3px 0; font-size:10px;">${locationConfig.address}</p>` : ''}
        <h3 style="margin:8px 0; color:#333; font-size:14px;">REÇU DE PAIEMENT</h3>
        <hr style="border:1px solid #000; margin:5px 0;"/>
        
        <div style="text-align:left; margin:8px 0; font-size:10px;">
          <p style="margin:2px 0;"><strong>Facture N°:</strong> ${selectedBill.billNumber || selectedBill._id?.slice(-6)}</p>
          <p style="margin:2px 0;"><strong>Date vente:</strong> ${billDate}</p>
          <p style="margin:2px 0;"><strong>Impression:</strong> ${currentDate}</p>
          ${selectedBill.tableNumber ? `<p style="margin:2px 0;"><strong>Table:</strong> ${selectedBill.tableNumber}</p>` : ''}
          ${selectedBill.customer?.name ? `<p style="margin:2px 0;"><strong>Client:</strong> ${selectedBill.customer.name}</p>` : ''}
          ${selectedBill.user?.name ? `<p style="margin:2px 0;"><strong>Vendeur:</strong> ${selectedBill.user.name}</p>` : ''}
          ${selectedBill.paymentMethod && !selectedBill.combinedPayment ? `<p style="margin:2px 0;"><strong>Paiement:</strong> ${selectedBill.paymentMethod}</p>` : ''}
          ${selectedBill.combinedPayment ? `<p style="margin:2px 0; color:#1565c0;"><strong>Paiement:</strong> Combiné (${selectedBill.combinedPaymentsDetail?.length || 0} méthodes)</p>` : ''}
        </div>
        
        ${combinedPaymentDetails}
        ${staffSection}
        
        <hr style="border:1px solid #000; margin:5px 0;"/>
        
        <div style="text-align:left; margin:8px 0;">
          ${itemsHtml}
        </div>
        
        <hr style="border:1px solid #000; margin:5px 0;"/>
        
        <div style="text-align:left; margin:8px 0; font-size:10px;">
          <div style="display:flex; justify-content:space-between;">
            <span>Total original:</span>
            <span>${totalOriginalTTC.toFixed(2)} DT</span>
          </div>
          ${totalDiscountAmount > 0 ? `
            <div style="display:flex; justify-content:space-between; color:#d32f2f;">
              <span>Montant remisé:</span>
              <span>-${totalDiscountAmount.toFixed(2)} DT</span>
            </div>
          ` : ''}
          <div style="display:flex; justify-content:space-between;">
            <span>Total TTC après remise:</span>
            <span>${totalTTC.toFixed(2)} DT</span>
          </div>
          <div style="display:flex; justify-content:space-between;">
            <span>Total HT:</span>
            <span>${totalHT.toFixed(2)} DT</span>
          </div>
          <div style="display:flex; justify-content:space-between;">
            <span>TVA (7%):</span>
            <span>${totalTVA.toFixed(2)} DT</span>
          </div>
          <div style="display:flex; justify-content:space-between; font-weight:bold; margin-top:3px; border-top:1px solid #000; padding-top:5px;">
            <span>Total à payer:</span>
            <span>${totalTTC.toFixed(2)} DT</span>
          </div>
        </div>
        
        <hr style="border:1px solid #000; margin:5px 0;"/>
        
        <div style="margin-top:10px; font-size:9px; color:#666;">
          <p>Merci pour votre visite !</p>
          <p>À bientôt chez ${locationConfig.name}</p>
        </div>
      </div>
    `;

    const printWindow = window.open('', '_blank', 'width=320,height=600,left=100,top=100');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Facture ${selectedBill.billNumber || ''}</title>
          <meta charset="UTF-8">
          <style>
            @media print {
              body { margin: 0; padding: 5px; }
              button { display: none !important; }
              * { -webkit-print-color-adjust: exact !important; }
          }
            body { font-family: 'Courier New', monospace; }
          </style>
        </head>
        <body>
          ${printableContent}
          <div style="text-align:center; margin-top:15px;">
            <button onclick="window.print()" style="
              padding: 8px 15px; 
              background: #007bff; 
              color: white; 
              border: none; 
              cursor: pointer; 
              border-radius: 4px;
              font-size: 12px;
            ">
              🖨️ Imprimer
            </button>
            <button onclick="window.close()" style="
              padding: 8px 15px; 
              background: #6c757d; 
              color: white; 
              border: none; 
              cursor: pointer; 
              border-radius: 4px;
              margin-left: 10px;
              font-size: 12px;
            ">
              Fermer
            </button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Préparation des dates
  const now = new Date();
  
  // 🔹 Obtenir la localisation de l'utilisateur
  const userLocId = getUserLocalisationId();
  console.log("📍 Localisation utilisateur pour filtrage :", userLocId);

  // 🔹 CORRIGÉ : Filtrer les factures du jour selon la localisation
  const dailyBills = useMemo(() => {
    if (!Array.isArray(bills) || bills.length === 0) return [];

    const todayBills = [];
    const todayStr = now.toISOString().split('T')[0];

    for (const bill of bills) {
      if (!bill.date) continue;
      
      try {
        const billDate = new Date(bill.date);
        if (isNaN(billDate.getTime())) continue;
        
        const billDateStr = billDate.toISOString().split('T')[0];
        if (billDateStr !== todayStr) continue;

        // 🔹 LOGIQUE SIMPLIFIÉE
        
        // 1. Admin voit tout
        if (currentUser?.role === "admin") {
          todayBills.push(bill);
          continue;
        }

        // 2. Si pas de localisation utilisateur, on ne filtre pas
        if (!userLocId || userLocId === 'admin') {
          todayBills.push(bill);
          continue;
        }

        // 3. Vérifier la localisation de la facture
        const billLocId = extractLocId(bill.localisation);
        if (billLocId && billLocId === userLocId) {
          todayBills.push(bill);
          continue;
        }

        // 4. Vérifier la localisation de l'utilisateur qui a créé la facture
        const billUserLocId = extractLocId(bill.user?.localisation);
        if (billUserLocId && billUserLocId === userLocId) {
          todayBills.push(bill);
          continue;
        }

      } catch (err) {
        console.warn("⚠️ Erreur traitement facture:", err);
        continue;
      }
    }

    console.log(`📊 ${todayBills.length} factures aujourd'hui avant regroupement`);
    console.log(`👤 Rôle utilisateur: ${currentUser?.role}`);
    console.log(`📍 Localisation utilisateur: ${userLocId || 'Non définie'}`);
    
    // 🔄 APPLIQUER LE REGROUPEMENT DES PAIEMENTS COMBINÉS
    const groupedBills = groupCombinedPayments(todayBills);
    console.log(`🔄 ${groupedBills.length} factures après regroupement des paiements combinés`);
    
    return groupedBills;
  }, [bills, currentUser, userLocId, now]);

  // Compter les additions personnels avec détails
  const staffAdditionDetails = useMemo(() => {
    const details = {
      count: 0,
      bills: [],
      totalAmount: 0,
      staffMembers: new Map()
    };
    
    dailyBills.forEach(bill => {
      if (bill.isStaffAddition) {
        details.count += 1;
        details.bills.push(bill);
        
        // Calculer le montant de cette facture
        const billTotalTTC = bill.items?.reduce((sum, item) => {
          const details = getProductDetailsWithDiscount(item, bill);
          return sum + (details.price * (item.quantity || 1));
        }, 0) || Number(bill.total || 0);
        
        details.totalAmount += billTotalTTC;
        
        // Compter par membre du personnel
        const staffInfo = getStaffInfo(bill);
        if (staffInfo) {
          const key = staffInfo.id;
          if (details.staffMembers.has(key)) {
            const current = details.staffMembers.get(key);
            details.staffMembers.set(key, {
              ...current,
              count: current.count + 1,
              totalAmount: current.totalAmount + billTotalTTC
            });
          } else {
            details.staffMembers.set(key, {
              ...staffInfo,
              count: 1,
              totalAmount: billTotalTTC
            });
          }
        }
      }
    });
    
    return details;
  }, [dailyBills, getProductDetailsWithDiscount, getStaffInfo]);

  // Calcul des items actifs et annulés AVEC REMISE - CORRIGÉ
  const { activeItems, cancelledItems } = useMemo(() => {
    const active = [];
    const cancelled = [];
    const processedItemKeys = new Set(); // Pour éviter les doublons

    dailyBills.forEach((bill) => {
      if (!Array.isArray(bill.items)) return;
      
      // Pour les paiements combinés unifiés, on évite le double comptage
      // en prenant seulement les items de la facture unifiée
      if (bill.unifiedCombinedBill) {
        bill.items.forEach((item) => {
          const itemKey = `${bill._id}_${getItemIdString(item)}_${item.quantity}_${item.price}`;
          
          if (!processedItemKeys.has(itemKey)) {
            const details = getProductDetailsWithDiscount(item, bill);

            const itemData = {
              bill,
              item,
              details,
              isCancelled: !!item.isCancelled,
              cancelReason: item.cancelReason || "",
              cancelledAt: item.cancelledAt || null,
              isCombinedPayment: bill.combinedPayment,
              combinedPaymentParts: bill.combinedPaymentParts
            };

            if (item.isCancelled) {
              cancelled.push(itemData);
            } else {
              active.push(itemData);
            }
            
            processedItemKeys.add(itemKey);
          }
        });
      } else {
        // Pour les factures normales, traiter tous les items
        bill.items.forEach((item) => {
          const itemKey = `${bill._id}_${getItemIdString(item)}_${item.quantity}_${item.price}`;
          
          if (!processedItemKeys.has(itemKey)) {
            const details = getProductDetailsWithDiscount(item, bill);

            const itemData = {
              bill,
              item,
              details,
              isCancelled: !!item.isCancelled,
              cancelReason: item.cancelReason || "",
              cancelledAt: item.cancelledAt || null,
              isCombinedPayment: false // Les factures normales ne sont pas combinées
            };

            if (item.isCancelled) {
              cancelled.push(itemData);
            } else {
              active.push(itemData);
            }
            
            processedItemKeys.add(itemKey);
          }
        });
      }
    });

    console.log(`📦 ${active.length} articles actifs, ${cancelled.length} articles annulés (sans doublons)`);
    console.log(`🔄 ${dailyBills.filter(b => b.unifiedCombinedBill).length} paiements combinés unifiés`);
    
    return { activeItems: active, cancelledItems: cancelled };
  }, [dailyBills, getProductDetailsWithDiscount]);

  // Totaux TTC AVEC REMISE
  const totalActiveAmountTTC = useMemo(() => {
    return activeItems.reduce((sum, { item, details }) => {
      const quantity = item.quantity || 1;
      return sum + (details.price * quantity);
    }, 0);
  }, [activeItems]);

  const totalCancelledAmountTTC = useMemo(() => {
    return cancelledItems.reduce((sum, { item, details }) => {
      const quantity = item.quantity || 1;
      return sum + (details.price * quantity);
    }, 0);
  }, [cancelledItems]);

  // Calcul des totaux originaux (sans remise) pour information
  const totalActiveOriginalTTC = useMemo(() => {
    return activeItems.reduce((sum, { item, details }) => {
      const quantity = item.quantity || 1;
      return sum + (details.originalPrice * quantity);
    }, 0);
  }, [activeItems]);

  const totalActiveDiscountAmount = useMemo(() => {
    return totalActiveOriginalTTC - totalActiveAmountTTC;
  }, [totalActiveOriginalTTC, totalActiveAmountTTC]);

  // Compter les additions personnels
  const staffAdditionCount = useMemo(() => {
    return dailyBills.filter(bill => bill.isStaffAddition).length;
  }, [dailyBills]);

  // Calcul des totaux HT et TVA AVEC REMISE
  const totalActiveAmountHT = useMemo(() => calculateHT(totalActiveAmountTTC), [totalActiveAmountTTC]);
  const totalActiveAmountTVA = useMemo(() => calculateTVA(totalActiveAmountTTC), [totalActiveAmountTTC]);

  const netAmountTTC = totalActiveAmountTTC - totalCancelledAmountTTC;
  const netAmountHT = calculateHT(netAmountTTC);
  const netAmountTVA = calculateTVA(netAmountTTC);

  // MODIFIER le tableau paymentStats pour éviter le double comptage
  const paymentStats = useMemo(() => {
    const stats = {};
    const processedBillIds = new Set(); // Pour éviter les doublons
    
    dailyBills.forEach(bill => {
      // Éviter de traiter plusieurs fois la même facture
      const billId = bill._id || bill.billNumber;
      if (processedBillIds.has(billId)) return;
      processedBillIds.add(billId);
      
      if (bill.unifiedCombinedBill) {
        // Traiter les paiements combinés unifiés comme une seule facture
        const totalTTC = bill.total || 0;
        const totalHT = calculateHT(totalTTC);
        const totalTVA = calculateTVA(totalTTC);
        
        // 🔥 CORRECTION: Utiliser combinedPaymentsDetail pour les paiements combinés
        if (bill.combinedPaymentsDetail && bill.combinedPaymentsDetail.length > 0) {
          bill.combinedPaymentsDetail.forEach(payment => {
            const method = payment.method;
            
            if (!stats[method]) {
              stats[method] = { 
                count: 0, 
                amountTTC: 0, 
                amountHT: 0, 
                amountTVA: 0,
                isStaffAdditionCount: 0,
                totalDiscountAmount: 0,
                combinedPaymentCount: 0
              };
            }
            
            // Calculer la proportion pour cette partie du paiement combiné
            const paymentAmount = payment.amount;
            const proportion = paymentAmount / (totalTTC || 1);
            
            // Calculer les totaux pour cette partie du paiement combiné
            stats[method].amountTTC += paymentAmount;
            stats[method].amountHT += totalHT * proportion;
            stats[method].amountTVA += totalTVA * proportion;
            stats[method].combinedPaymentCount += 1;
            
            // Compter les additions personnels
            if (bill.isStaffAddition) {
              stats[method].isStaffAdditionCount += 1;
            }
            
            // Calculer la remise pour cette partie
            const billDiscountAmount = bill.items?.reduce((sum, item) => {
              const details = getProductDetailsWithDiscount(item, bill);
              const originalPrice = details.originalPrice || details.price;
              const discountPrice = details.price;
              const quantity = item.quantity || 1;
              const itemDiscount = (originalPrice - discountPrice) * quantity;
              return sum + (itemDiscount * proportion);
            }, 0) || 0;
            
            stats[method].totalDiscountAmount += billDiscountAmount;
          });
          
          // Compter la facture combinée comme une seule facture pour chaque méthode
          bill.combinedPaymentsDetail.forEach(payment => {
            const method = payment.method;
            if (stats[method]) {
              stats[method].count += 1;
            }
          });
        } else {
          // Fallback si pas de combinedPaymentsDetail
          const method = bill.paymentMethod || "Combiné";
          
          if (!stats[method]) {
            stats[method] = { 
              count: 0, 
              amountTTC: 0, 
              amountHT: 0, 
              amountTVA: 0,
              isStaffAdditionCount: 0,
              totalDiscountAmount: 0,
              combinedPaymentCount: 0
            };
          }
          
          stats[method].count += 1;
          stats[method].amountTTC += totalTTC;
          stats[method].amountHT += totalHT;
          stats[method].amountTVA += totalTVA;
          stats[method].combinedPaymentCount += 1;
          
          if (bill.isStaffAddition) {
            stats[method].isStaffAdditionCount += 1;
          }
        }
        
      } else {
        // Facture normale (non combinée)
        const method = bill.paymentMethod || "Non spécifié";
        
        if (!stats[method]) {
          stats[method] = { 
            count: 0, 
            amountTTC: 0, 
            amountHT: 0, 
            amountTVA: 0,
            isStaffAdditionCount: 0,
            totalDiscountAmount: 0,
            combinedPaymentCount: 0
          };
        }
        
        // Calculer le total TTC de la facture AVEC REMISE
        const billTotalTTC = bill.items?.reduce((sum, item) => {
          const details = getProductDetailsWithDiscount(item, bill);
          return sum + (details.price * (item.quantity || 1));
        }, 0) || Number(bill.total || 0);
        
        const billTotalHT = calculateHT(billTotalTTC);
        const billTotalTVA = calculateTVA(billTotalTTC);
        
        stats[method].count += 1;
        stats[method].amountTTC += billTotalTTC;
        stats[method].amountHT += billTotalHT;
        stats[method].amountTVA += billTotalTVA;
        
        // Compter les additions personnels
        if (bill.isStaffAddition) {
          stats[method].isStaffAdditionCount += 1;
        }
        
        // Calculer le montant total des remises pour cette facture
        const billDiscountAmount = bill.items?.reduce((sum, item) => {
          const details = getProductDetailsWithDiscount(item, bill);
          const originalPrice = details.originalPrice || details.price;
          const discountPrice = details.price;
          const quantity = item.quantity || 1;
          return sum + ((originalPrice - discountPrice) * quantity);
        }, 0) || 0;
        
        stats[method].totalDiscountAmount += billDiscountAmount;
      }
    });
    
    return stats;
  }, [dailyBills, getProductDetailsWithDiscount]);

  // Produits vendus par catégorie - Prix TTC AVEC REMISE
  const productSalesByCategory = useMemo(() => {
    const salesByCategory = {};
    const processedProductKeys = new Set(); // Pour éviter les doublons
    
    activeItems.forEach(({ bill, item, details }) => {
      const productKey = `${bill._id}_${getItemIdString(item)}_${item.quantity}_${item.price}`;
      
      // Éviter les doublons
      if (processedProductKeys.has(productKey)) return;
      processedProductKeys.add(productKey);
      
      const category = details.category || "Non catégorisé";
      const productName = details.name || details.parent || "Produit inconnu";
      const categoryProductKey = `${category}_${productName}`.toLowerCase();
      const quantity = item.quantity || 1;
      const priceTTC = details.price; // Prix déjà avec remise
      const originalPrice = details.originalPrice || priceTTC;
      const totalTTC = priceTTC * quantity;
      const totalOriginal = originalPrice * quantity;
      const totalHT = calculateHT(totalTTC);
      const totalTVA = calculateTVA(totalTTC);
      const discountAmount = totalOriginal - totalTTC;
      const isStaffAddition = details.isStaffAddition;
      const isCombinedPayment = bill.unifiedCombinedBill || false;
      
      if (!salesByCategory[category]) {
        salesByCategory[category] = {
          products: {},
          totalTTC: 0,
          totalHT: 0,
          totalTVA: 0,
          totalQuantity: 0,
          totalDiscountAmount: 0,
          staffAdditionCount: 0,
          combinedPaymentCount: 0
        };
      }
      
      if (!salesByCategory[category].products[categoryProductKey]) {
        salesByCategory[category].products[categoryProductKey] = {
          name: productName,
          quantity: 0,
          unitPriceTTC: priceTTC,
          originalUnitPrice: originalPrice,
          totalTTC: 0,
          totalHT: 0,
          totalTVA: 0,
          totalDiscount: 0,
          isStaffAddition: isStaffAddition,
          isCombinedPayment: isCombinedPayment,
          staffAdditionCount: isStaffAddition ? 1 : 0,
          combinedPaymentCount: isCombinedPayment ? 1 : 0
        };
      }
      
      salesByCategory[category].products[categoryProductKey].quantity += quantity;
      salesByCategory[category].products[categoryProductKey].totalTTC += totalTTC;
      salesByCategory[category].products[categoryProductKey].totalHT += totalHT;
      salesByCategory[category].products[categoryProductKey].totalTVA += totalTVA;
      salesByCategory[category].products[categoryProductKey].totalDiscount += discountAmount;
      
      if (isStaffAddition) {
        salesByCategory[category].products[categoryProductKey].staffAdditionCount += 1;
        salesByCategory[category].staffAdditionCount += 1;
      }
      
      if (isCombinedPayment) {
        salesByCategory[category].products[categoryProductKey].combinedPaymentCount += 1;
        salesByCategory[category].combinedPaymentCount += 1;
      }
      
      salesByCategory[category].totalTTC += totalTTC;
      salesByCategory[category].totalHT += totalHT;
      salesByCategory[category].totalTVA += totalTVA;
      salesByCategory[category].totalQuantity += quantity;
      salesByCategory[category].totalDiscountAmount += discountAmount;
    });
    
    return salesByCategory;
  }, [activeItems]);

  // Génération PDF - AVEC REMISE AFFICHÉE
  const generatePDF = () => {
    try {
      const userLocIdLocal = getUserLocalisationId();
      const userLocationName = locations.find((loc) => 
        extractLocId(loc._id) === userLocIdLocal
      )?.name || currentUser?.localisation?.name || "Localisation inconnue";

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Header
      doc.setFontSize(16);
      doc.setTextColor(40, 40, 40);
      doc.text("RAPPORT DE CAISSE DÉTAILLÉ", 105, 20, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Date: ${new Date().toLocaleDateString("fr-FR")}`, 105, 30, { align: "center" });
      doc.text(`Localisation: ${userLocationName}`, 105, 37, { align: "center" });
      doc.text(`Généré par: ${currentUser?.name || "Utilisateur"}`, 105, 44, { align: "center" });

      let yPosition = 55;

      // Statistiques de paiement (TTC avec remise)
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 150);
      doc.text("STATISTIQUES DE PAIEMENT", 14, yPosition);
      yPosition += 8;

      const paymentStatsData = Object.entries(paymentStats).map(([method, stats]) => [
        method,
        stats.count.toString(),
        `${stats.amountTTC.toFixed(2)} DT TTC`,
        stats.totalDiscountAmount > 0 ? `-${stats.totalDiscountAmount.toFixed(2)} DT` : "-",
        stats.isStaffAdditionCount > 0 ? `${stats.isStaffAdditionCount}` : "0",
        stats.combinedPaymentCount > 0 ? `${stats.combinedPaymentCount}` : "0"
      ]);

      if (paymentStatsData.length > 0) {
        doc.autoTable({
          startY: yPosition,
          head: [["Méthode", "Nombre", "Montant TTC", "Remise", "Add. Personnel", "Combiné"]],
          body: paymentStatsData,
          theme: "grid",
          headStyles: { fillColor: [0, 0, 150], textColor: 255 },
          margin: { top: 2 },
          styles: { fontSize: 9 }
        });
        yPosition = doc.lastAutoTable.finalY + 12;
      }

      // Total recette du jour avec remise
      doc.setFontSize(14);
      doc.setTextColor(220, 0, 0);
      doc.text(`TOTAL RECETTE DU JOUR: ${netAmountTTC.toFixed(2)} DT TTC`, 105, yPosition, { align: "center" });
      yPosition += 6;
      
      if (totalActiveDiscountAmount > 0) {
        doc.setFontSize(10);
        doc.setTextColor(200, 0, 0);
        doc.text(`Dont remise totale: -${totalActiveDiscountAmount.toFixed(2)} DT`, 105, yPosition, { align: "center" });
        yPosition += 5;
      }
      
      if (staffAdditionCount > 0) {
        doc.setTextColor(255, 152, 0);
        doc.text(`Additions personnels: ${staffAdditionCount}`, 105, yPosition, { align: "center" });
        yPosition += 5;
      }
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Dont HT: ${netAmountHT.toFixed(2)} DT | TVA (7%): ${netAmountTVA.toFixed(2)} DT`, 105, yPosition, { align: "center" });
      yPosition += 15;

      // Produits vendus par catégorie avec remise
      doc.setFontSize(12);
      doc.setTextColor(0, 100, 0);
      doc.text("PRODUITS VENDUS PAR CATÉGORIE", 14, yPosition);
      yPosition += 8;

      Object.entries(productSalesByCategory).forEach(([category, data]) => {
        if (data.totalQuantity > 0) {
          doc.setFontSize(11);
          doc.setTextColor(0, 0, 0);
          doc.text(`Catégorie: ${category}`, 14, yPosition);
          yPosition += 6;

          const categoryProducts = Object.values(data.products).map(product => [
            product.name,
            product.quantity.toString(),
            `${product.unitPriceTTC.toFixed(2)} DT TTC`,
            `${product.totalTTC.toFixed(2)} DT TTC`,
            product.totalDiscount > 0 ? `-${product.totalDiscount.toFixed(2)} DT` : "-"
          ]);

          if (categoryProducts.length > 0) {
            doc.autoTable({
              startY: yPosition,
              head: [["Produit", "Qté", "Prix U. TTC", "Total TTC", "Remise"]],
              body: categoryProducts,
              theme: "grid",
              headStyles: { fillColor: [0, 100, 0], textColor: 255 },
              margin: { top: 2 },
              styles: { fontSize: 8 },
              columnStyles: {
                0: { cellWidth: 60 },
                1: { cellWidth: 15 },
                2: { cellWidth: 30 },
                3: { cellWidth: 35 },
                4: { cellWidth: 25 }
              }
            });
            yPosition = doc.lastAutoTable.finalY + 8;
          }

          // Sous-total catégorie avec remise
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          const categoryInfo = `Sous-total ${category}: ${data.totalTTC.toFixed(2)} DT TTC (HT: ${data.totalHT.toFixed(2)} DT + TVA: ${data.totalTVA.toFixed(2)} DT)`;
          const discountInfo = data.totalDiscountAmount > 0 ? ` | Remise: -${data.totalDiscountAmount.toFixed(2)} DT` : "";
          const staffInfo = data.staffAdditionCount > 0 ? ` | Add. perso: ${data.staffAdditionCount}` : "";
          const combinedInfo = data.combinedPaymentCount > 0 ? ` | Paiements combinés: ${data.combinedPaymentCount}` : "";
          
          doc.text(categoryInfo + discountInfo + staffInfo + combinedInfo, 14, yPosition);
          yPosition += 12;
        }
      });

      // Récapitulatif général avec remise
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("RÉCAPITULATIF GÉNÉRAL", 105, yPosition, { align: "center" });
      yPosition += 10;

      doc.setFontSize(10);
      doc.setTextColor(0, 100, 0);
      doc.text(`Ventes actives: ${totalActiveAmountTTC.toFixed(2)} DT TTC`, 14, yPosition);
      if (totalActiveDiscountAmount > 0) {
        doc.setTextColor(200, 0, 0);
        doc.text(`(Dont remise: -${totalActiveDiscountAmount.toFixed(2)} DT)`, 30, yPosition + 4);
      }
      doc.setTextColor(100, 100, 100);
      doc.text(`(HT: ${totalActiveAmountHT.toFixed(2)} DT + TVA: ${totalActiveAmountTVA.toFixed(2)} DT)`, 30, yPosition + 8);
      yPosition += 15;

      if (cancelledItems.length > 0) {
        doc.setTextColor(200, 0, 0);
        doc.text(`Articles annulés: ${totalCancelledAmountTTC.toFixed(2)} DT TTC`, 14, yPosition);
        yPosition += 8;
      }

      doc.setFontSize(11);
      doc.setTextColor(0, 0, 150);
      doc.text(`NET À ENCAISSER: ${netAmountTTC.toFixed(2)} DT TTC`, 14, yPosition);
      yPosition += 5;
      doc.setTextColor(100, 100, 100);
      doc.text(`(HT: ${netAmountHT.toFixed(2)} DT + TVA: ${netAmountTVA.toFixed(2)} DT)`, 30, yPosition);
      yPosition += 10;

      // Stats supplémentaires
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`Total articles: ${activeItems.length}`, 14, yPosition);
      yPosition += 6;
      doc.text(`Total factures: ${dailyBills.length}`, 14, yPosition);
      yPosition += 6;
      if (staffAdditionCount > 0) {
        doc.text(`Additions personnels: ${staffAdditionCount}`, 14, yPosition);
        yPosition += 6;
      }
      
      const combinedPaymentCount = dailyBills.filter(b => b.unifiedCombinedBill).length;
      if (combinedPaymentCount > 0) {
        doc.text(`Paiements combinés: ${combinedPaymentCount}`, 14, yPosition);
        yPosition += 6;
      }
      
      const annulationRate = activeItems.length + cancelledItems.length > 0
        ? ((cancelledItems.length / (activeItems.length + cancelledItems.length)) * 100).toFixed(1)
        : 0;
      doc.text(`Taux d'annulation: ${annulationRate}%`, 14, yPosition);

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Rapport généré le ${new Date().toLocaleString("fr-FR")}`, 105, 285, { align: "center" });

      doc.save(`rapport-caisse-${new Date().toLocaleDateString("fr-FR")}.pdf`);
      
    } catch (error) {
      console.error("❌ Erreur génération PDF:", error);
      alert("Erreur lors de la génération du PDF");
    }
  };

  // Impression récapitulatif détaillé - AVEC REMISE
  const printDetailedSummary = () => {
    const locationConfig = getLocationConfig();
    const currentDate = new Date().toLocaleString();
    const today = new Date().toLocaleDateString('fr-FR');
    
    // Construction HTML des catégories AVEC REMISE
    let categoriesHtml = '';
    Object.entries(productSalesByCategory).forEach(([category, data]) => {
      if (data.totalQuantity > 0) {
        let productsHtml = '';
        Object.values(data.products).forEach(product => {
          const discountInfo = product.totalDiscount > 0 ? 
            `<div style="font-size:9px; color:#d32f2f;">
              Remise: -${product.totalDiscount.toFixed(2)} DT
              ${product.isStaffAddition ? ' (Personnel)' : ''}
              ${product.isCombinedPayment ? ' 🔄 Combiné' : ''}
            </div>` : '';
          
          productsHtml += `
            <div style="margin:4px 0; padding-left:15px;">
              <div style="display:flex; justify-content:space-between; font-size:10px;">
                <span style="flex:2;">${product.name}</span>
                <span style="flex:1; text-align:center;">Qté: ${product.quantity}</span>
                <span style="flex:1; text-align:center;">${product.unitPriceTTC.toFixed(2)} DT TTC</span>
                <span style="flex:1; text-align:right;">${product.totalTTC.toFixed(2)} DT TTC</span>
              </div>
              <div style="font-size:9px; color:#666; padding-left:20px;">
                HT: ${product.totalHT.toFixed(2)} DT + TVA: ${product.totalTVA.toFixed(2)} DT
              </div>
              ${discountInfo}
            </div>
          `;
        });
        
        const categoryDiscountInfo = data.totalDiscountAmount > 0 ? 
          `<div style="font-size:10px; color:#d32f2f; margin:5px 0;">
            Remise catégorie: -${data.totalDiscountAmount.toFixed(2)} DT
            ${data.staffAdditionCount > 0 ? ` (${data.staffAdditionCount} addition${data.staffAdditionCount !== 1 ? 's' : ''} personnel)` : ''}
            ${data.combinedPaymentCount > 0 ? ` (${data.combinedPaymentCount} paiement${data.combinedPaymentCount !== 1 ? 's' : ''} combiné)` : ''}
          </div>` : '';
        
        categoriesHtml += `
          <div style="margin:10px 0; border:1px solid #ddd; border-radius:5px; padding:8px;">
            <div style="font-weight:bold; background:#f0f0f0; padding:4px 8px; font-size:11px; border-radius:3px;">
              🍽️ Catégorie : ${category.toUpperCase()}
            </div>
            ${productsHtml}
            ${categoryDiscountInfo}
            <div style="display:flex; justify-content:space-between; margin:4px 0; padding:4px 8px; font-weight:bold; font-size:10px; background:#f8f9fa; border-radius:3px;">
              <span>Sous-total:</span>
              <span>
                <div>${data.totalTTC.toFixed(2)} DT TTC</div>
                <div style="font-weight:normal; font-size:9px;">(HT: ${data.totalHT.toFixed(2)} DT + TVA: ${data.totalTVA.toFixed(2)} DT)</div>
              </span>
            </div>
          </div>
        `;
      }
    });
    
    // Statistiques de paiement HTML AVEC REMISE
    let paymentStatsHtml = '';
    Object.entries(paymentStats).forEach(([method, stats]) => {
      const discountInfo = stats.totalDiscountAmount > 0 ? 
        `<div style="font-size:9px; color:#d32f2f; margin-top:2px;">
          Remise: -${stats.totalDiscountAmount.toFixed(2)} DT
        </div>` : '';
      
      const staffInfo = stats.isStaffAdditionCount > 0 ?
        `<div style="font-size:9px; color:#ff9800; margin-top:2px;">
          👥 ${stats.isStaffAdditionCount} addition${stats.isStaffAdditionCount !== 1 ? 's' : ''} personnel
        </div>` : '';
      
      const combinedInfo = stats.combinedPaymentCount > 0 ?
        `<div style="font-size:9px; color:#9c27b0; margin-top:2px;">
          🔄 ${stats.combinedPaymentCount} paiement${stats.combinedPaymentCount !== 1 ? 's' : ''} combiné${stats.combinedPaymentCount !== 1 ? 's' : ''}
        </div>` : '';
      
      paymentStatsHtml += `
        <div style="margin:4px 0; padding:6px; background:#f8f9fa; border-radius:4px;">
          <div style="display:flex; justify-content:space-between; font-size:10px;">
            <span><strong>${method}:</strong></span>
            <span><strong>${stats.amountTTC.toFixed(2)} DT TTC</strong> (${stats.count} facture${stats.count !== 1 ? 's' : ''})</span>
          </div>
          ${discountInfo}
          ${staffInfo}
          ${combinedInfo}
          <div style="font-size:9px; color:#666; text-align:right;">
            HT: ${stats.amountHT.toFixed(2)} DT + TVA: ${stats.amountTVA.toFixed(2)} DT
          </div>
        </div>
      `;
    });
    
    const printableContent = `
      <div style="font-family:'Courier New', monospace; text-align:center; max-width:340px; margin:0 auto; font-size:11px;">
        <div style="text-align:center; margin-bottom:8px;">
          <img src="${locationConfig.logo}" alt="Logo" style="max-width:120px; max-height:40px;" onerror="this.style.display='none'" />
        </div>
        <h1 style="margin:0; font-size:14px;">${locationConfig.name}</h1>
        ${locationConfig.address ? `<p style="margin:2px 0; font-size:9px;">${locationConfig.address}</p>` : ''}
        <h3 style="margin:6px 0; color:#333; font-size:12px;">RAPPORT DE CAISSE DÉTAILLÉ</h3>
        <hr style="border:1px solid #000; margin:4px 0;"/>
        
        <div style="text-align:left; margin:6px 0; font-size:10px;">
          <p style="margin:1px 0;"><strong>📅 Date:</strong> ${today}</p>
          <p style="margin:1px 0;"><strong>⏰ Heure:</strong> ${new Date().toLocaleTimeString('fr-FR')}</p>
          <p style="margin:1px 0;"><strong>👤 Généré par:</strong> ${currentUser?.name || "Utilisateur"}</p>
          <p style="margin:1px 0;"><strong>📍 Localisation:</strong> ${currentUser?.localisation?.name || "Non définie"}</p>
          <p style="margin:1px 0;"><strong>🖨️ Impression:</strong> ${currentDate}</p>
        </div>
        
        <hr style="border:1px solid #000; margin:4px 0;"/>
        
        <div style="margin:10px 0;">
          <h4 style="margin:6px 0; text-align:left; font-size:11px; color:#007bff;">💳 STATISTIQUES DE PAIEMENT</h4>
          ${paymentStatsHtml || '<p style="text-align:center; color:#999; font-size:10px;">Aucun paiement enregistré</p>'}
        </div>
        
        <hr style="border:1px solid #000; margin:4px 0;"/>
        
        <div style="margin:10px 0; background:#fff3cd; padding:10px; border-radius:5px; border:1px solid #ffeaa7;">
          <h4 style="margin:6px 0; text-align:center; font-weight:bold; color:#d32f2f; font-size:12px;">
            💰 TOTAL RECETTE DU JOUR
          </h4>
          <div style="text-align:center; font-size:16px; font-weight:bold; color:#d32f2f;">
            ${netAmountTTC.toFixed(2)} DT TTC
          </div>
          ${totalActiveDiscountAmount > 0 ? `
            <div style="text-align:center; font-size:11px; color:#d32f2f; margin-top:3px;">
              Dont remise totale: -${totalActiveDiscountAmount.toFixed(2)} DT
            </div>
          ` : ''}
          ${staffAdditionCount > 0 ? `
            <div style="text-align:center; font-size:11px; color:#ff9800; margin-top:3px;">
              👥 ${staffAdditionCount} addition${staffAdditionCount !== 1 ? 's' : ''} personnel
            </div>
          ` : ''}
          <div style="text-align:center; font-size:10px; color:#666; margin-top:2px;">
            HT: ${netAmountHT.toFixed(2)} DT + TVA: ${netAmountTVA.toFixed(2)} DT
          </div>
        </div>
        
        <hr style="border:1px solid #000; margin:4px 0;"/>
        
        <div style="margin:10px 0;">
          <h4 style="margin:6px 0; text-align:left; font-size:11px; color:#28a745;">📦 PRODUITS VENDUS PAR CATÉGORIE</h4>
          ${categoriesHtml || '<p style="text-align:center; color:#999; font-size:10px;">Aucun produit vendu aujourd\'hui</p>'}
        </div>
        
        <hr style="border:1px solid #000; margin:4px 0;"/>
        
        <div style="margin:10px 0;">
          <h4 style="margin:6px 0; text-align:left; font-size:11px; color:#6c757d;">📊 RÉCAPITULATIF GÉNÉRAL</h4>
          <div style="display:flex; justify-content:space-between; margin:4px 0; padding:6px; background:#f8f9fa; border-radius:4px;">
            <span>Ventes actives:</span>
            <span>
              <div>${totalActiveAmountTTC.toFixed(2)} DT TTC</div>
              ${totalActiveDiscountAmount > 0 ? `
                <div style="font-size:9px; color:#d32f2f;">
                  Dont remise: -${totalActiveDiscountAmount.toFixed(2)} DT
                </div>
              ` : ''}
              <div style="font-size:9px;">HT: ${totalActiveAmountHT.toFixed(2)} DT</div>
            </span>
          </div>
          ${cancelledItems.length > 0 ? `
            <div style="display:flex; justify-content:space-between; margin:4px 0; padding:6px; background:#f8f9fa; border-radius:4px;">
              <span>Articles annulés:</span>
              <span>
                <div>${totalCancelledAmountTTC.toFixed(2)} DT TTC</div>
                <div style="font-size:9px;">HT: ${calculateHT(totalCancelledAmountTTC).toFixed(2)} DT</div>
              </span>
            </div>
          ` : ''}
          <div style="display:flex; justify-content:space-between; margin:6px 0; padding:8px; font-weight:bold; font-size:12px; background:#007bff; color:white; border-radius:4px;">
            <span>Net à encaisser:</span>
            <span>${netAmountTTC.toFixed(2)} DT TTC</span>
          </div>
          <div style="font-size:9px; color:#666; text-align:right; margin-bottom:10px;">
            HT: ${netAmountHT.toFixed(2)} DT + TVA: ${netAmountTVA.toFixed(2)} DT
          </div>
          <div style="display:flex; justify-content:space-between; margin:4px 0; font-size:9px; color:#666;">
            <span>Total articles:</span>
            <span>${activeItems.length}</span>
          </div>
          <div style="display:flex; justify-content:space-between; margin:2px 0; font-size:9px; color:#666;">
            <span>Total factures:</span>
            <span>${dailyBills.length}</span>
          </div>
          ${staffAdditionCount > 0 ? `
            <div style="display:flex; justify-content:space-between; margin:2px 0; font-size:9px; color:#ff9800;">
              <span>Additions personnels:</span>
              <span>${staffAdditionCount}</span>
            </div>
          ` : ''}
        </div>
        
        <hr style="border:1px solid #000; margin:4px 0;"/>
        
        <div style="margin-top:10px; font-size:9px; color:#666;">
          <p><strong>Signature responsable:</strong></p>
          <div style="margin-top:15px; border-top:1px solid #000; padding-top:8px; height:40px;">
            ___________________
          </div>
          <p style="margin-top:5px;">Document imprimé le ${currentDate}</p>
        </div>
      </div>
    `;

    const printWindow = window.open('', '_blank', 'width=360,height=650,left=100,top=100');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Rapport Caisse - ${today}</title>
          <meta charset="UTF-8">
          <style>
            @media print {
              body { margin: 0; padding: 5px; }
              button { display: none !important; }
              * { -webkit-print-color-adjust: exact !important; }
            }
            body { font-family: 'Courier New', monospace; }
          </style>
        </head>
        <body>
          ${printableContent}
          <div style="text-align:center; margin-top:15px;">
            <button onclick="window.print()" style="
              padding: 8px 15px; 
              background: #28a745; 
              color: white; 
              border: none; 
              cursor: pointer; 
              border-radius: 4px;
              font-size: 12px;
            ">
              🖨️ Imprimer le rapport
            </button>
            <button onclick="window.close()" style="
              padding: 8px 15px; 
              background: #6c757d; 
              color: white; 
              border: none; 
              cursor: pointer; 
              border-radius: 4px;
              margin-left: 10px;
              font-size: 12px;
            ">
              Fermer
            </button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Fonction pour rafraîchir les données
  const refreshData = () => {
    setBillsCache({});
    setCacheTimestamp(null);
    fetchAllData();
  };

  return (
    <div className="bills-filter">
      {/* Popup détail facture */}
      {showBillPopup && selectedBill && (
        <div className="bill-popup-overlay">
          <div className="bill-popup">
            <div className="bill-popup-header">
              <h3>Détail Facture #{selectedBill.billNumber || selectedBill._id?.slice(-6)}</h3>
              <button onClick={closeBillPopup} className="close-popup-btn">
                ✕
              </button>
            </div>
            
            <div className="bill-popup-content">
              <div className="bill-info">
                <p><strong>Date:</strong> {new Date(selectedBill.date).toLocaleString('fr-FR')}</p>
                <p><strong>Paiement:</strong> {selectedBill.paymentMethod || "Non spécifié"}</p>
                <p><strong>Table:</strong> {selectedBill.tableNumber || "N/A"}</p>
                <p><strong>Vendeur:</strong> {selectedBill.user?.name || "N/A"}</p>
                
                {/* Affichage de la répartition des paiements combinés */}
                {selectedBill.combinedPayment && renderCombinedPaymentBreakdown(selectedBill)}
                
                {selectedBill.isStaffAddition && (
                  <div className="staff-info-section">
                    <p style={{color: '#ff9800', fontWeight: 'bold', marginBottom: '5px'}}>
                      <strong>Addition Personnel:</strong> Oui
                    </p>
                    {(() => {
                      const staffInfo = getStaffInfo(selectedBill);
                      if (staffInfo) {
                        return (
                          <div className="staff-details" style={{
                            backgroundColor: '#fff3cd',
                            padding: '10px',
                            borderRadius: '5px',
                            marginTop: '5px'
                          }}>
                            <p style={{margin: '3px 0', fontSize: '13px'}}>
                              <strong>Nom:</strong> {staffInfo.name}
                            </p>
                            <p style={{margin: '3px 0', fontSize: '13px'}}>
                              <strong>Matricule:</strong> {staffInfo.matricule}
                            </p>
                            {staffInfo.department && (
                              <p style={{margin: '3px 0', fontSize: '13px'}}>
                                <strong>Département:</strong> {staffInfo.department}
                              </p>
                            )}
                            <p style={{margin: '3px 0', fontSize: '13px', color: '#d32f2f'}}>
                              <strong>Remise appliquée:</strong> 20%
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>
              
              {/* 🔥 TABLEAU CORRIGÉ: Ajout de la colonne Prix global */}
              <table className="bill-items-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Qté</th>
                    <th>Prix U. TTC</th>
                    <th>Prix Global</th>
                    <th>Remise</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBill.items.map((item, index) => {
                    const details = getProductDetailsWithDiscount(item, selectedBill);
                    const unitPriceTTC = details.price;
                    const globalPrice = unitPriceTTC * (item.quantity || 1);
                    const originalGlobalPrice = details.originalPrice * (item.quantity || 1);
                    const totalHT = calculateHT(globalPrice);
                    const tva = calculateTVA(globalPrice);
                    const discountAmount = originalGlobalPrice - globalPrice;
                    
                    return (
                      <tr key={index} className={details.hasDiscount ? "has-discount" : ""}>
                        <td>{details.name || details.parent || "Produit"}</td>
                        <td>{item.quantity || 1}</td>
                        <td>
                          <div>{unitPriceTTC.toFixed(2)} DT TTC</div>
                          {details.originalPrice !== unitPriceTTC && (
                            <div style={{fontSize: '10px', color: '#666', textDecoration: 'line-through'}}>
                              {details.originalPrice.toFixed(2)} DT
                            </div>
                          )}
                        </td>
                        <td>
                          <div>{globalPrice.toFixed(2)} DT TTC</div>
                          {originalGlobalPrice !== globalPrice && (
                            <div style={{fontSize: '10px', color: '#666', textDecoration: 'line-through'}}>
                              {originalGlobalPrice.toFixed(2)} DT
                            </div>
                          )}
                          <div style={{fontSize: '11px', color: '#666'}}>
                            (HT: {totalHT.toFixed(2)} DT + TVA: {tva.toFixed(2)} DT)
                          </div>
                        </td>
                        <td>
                          {details.discountApplied > 0 ? (
                            <div style={{color: '#d32f2f'}}>
                              <div>-{details.discountApplied}%</div>
                              <div style={{fontSize: '10px'}}>
                                -{discountAmount.toFixed(2)} DT
                                {details.isStaffAddition && (
                                  <div style={{fontSize: '9px', color: '#ff9800'}}>
                                    👥 Personnel
                                  </div>
                                )}
                                {selectedBill.combinedPayment && (
                                  <div style={{fontSize: '9px', color: '#9c27b0'}}>
                                    🔄 Combiné
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div style={{color: '#666', fontSize: '12px'}}>-</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              <div className="bill-total">
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}>
                  <span>Total original:</span>
                  <span>{selectedBill.items.reduce((sum, item) => {
                    const details = getProductDetailsWithDiscount(item, selectedBill);
                    return sum + (details.originalPrice * (item.quantity || 1));
                  }, 0).toFixed(2)} DT</span>
                </div>
                
                {selectedBill.items.reduce((sum, item) => {
                  const details = getProductDetailsWithDiscount(item, selectedBill);
                  const originalTotal = details.originalPrice * (item.quantity || 1);
                  const discountedTotal = details.price * (item.quantity || 1);
                  return sum + (originalTotal - discountedTotal);
                }, 0) > 0 && (
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px', color: '#d32f2f'}}>
                    <span>Remise totale:</span>
                    <span>-{selectedBill.items.reduce((sum, item) => {
                      const details = getProductDetailsWithDiscount(item, selectedBill);
                      const originalTotal = details.originalPrice * (item.quantity || 1);
                      const discountedTotal = details.price * (item.quantity || 1);
                      return sum + (originalTotal - discountedTotal);
                    }, 0).toFixed(2)} DT</span>
                  </div>
                )}
                
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}>
                  <span>Total TTC après remise:</span>
                  <span>{selectedBill.items.reduce((sum, item) => {
                    const details = getProductDetailsWithDiscount(item, selectedBill);
                    return sum + (details.price * (item.quantity || 1));
                  }, 0).toFixed(2)} DT</span>
                </div>
                
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}>
                  <span>Total HT:</span>
                  <span>{calculateHT(selectedBill.items.reduce((sum, item) => {
                    const details = getProductDetailsWithDiscount(item, selectedBill);
                    return sum + (details.price * (item.quantity || 1));
                  }, 0)).toFixed(2)} DT</span>
                </div>
                
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}>
                  <span>TVA (7%):</span>
                  <span>{calculateTVA(selectedBill.items.reduce((sum, item) => {
                    const details = getProductDetailsWithDiscount(item, selectedBill);
                    return sum + (details.price * (item.quantity || 1));
                  }, 0)).toFixed(2)} DT</span>
                </div>
              </div>
            </div>
            
            <div className="bill-popup-actions">
              <button onClick={printBill} className="print-bill-btn">
                🖨️ Imprimer
              </button>
              <button onClick={closeBillPopup} className="close-popup-btn">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <h2>🧾 Rapport de Caisse</h2>
          <p className="report-date">
            {new Date().toLocaleDateString("fr-FR", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <div className="location-info">
            <small>
              📍 Localisation : <strong>{currentUser?.localisation?.name || "Non définie"}</strong>
              {currentUser?.role === "admin" && " (Administrateur - voit toutes les localisations)"}
            </small>
          </div>
        </div>
        <div className="header-buttons">
          <button onClick={refreshData} className="refresh-btn" disabled={loading}>
            {loading ? "⏳" : "🔄"} Rafraîchir
          </button>
          <button onClick={generatePDF} className="pdf-button" disabled={loading}>
            📄 PDF
          </button>
          <button onClick={printDetailedSummary} className="print-ticket-button" disabled={loading}>
            🖨️ Rapport
          </button>
        </div>
      </div>

      {/* Cartes de résumé - AFFICHER AVEC REMISE */}
      <div className="summary-cards">
        <div className="summary-card active">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>Ventes Actives</h3>
            <p className="amount">{totalActiveAmountTTC.toFixed(2)} DT TTC</p>
            <p className="count">
              {activeItems.length} articles<br/>
              {totalActiveDiscountAmount > 0 && (
                <small style={{fontSize: '12px', color: '#d32f2f'}}>
                  Remise: -{totalActiveDiscountAmount.toFixed(2)} DT
                </small>
              )}
              {staffAdditionCount > 0 && (
                <small style={{fontSize: '12px', color: '#ff9800', display: 'block', marginTop: '5px'}}>
                  👥 {staffAdditionCount} addition{staffAdditionCount !== 1 ? 's' : ''} personnel
                </small>
              )}
              <small style={{fontSize: '12px', color: '#666', display: 'block', marginTop: '5px'}}>
                HT: {totalActiveAmountHT.toFixed(2)} DT
              </small>
            </p>
          </div>
        </div>
        
        <div className="summary-card cancelled">
          <div className="card-icon">❌</div>
          <div className="card-content">
            <h3>Annulations</h3>
            <p className="amount">{totalCancelledAmountTTC.toFixed(2)} DT TTC</p>
            <p className="count">
              {cancelledItems.length} articles<br/>
              <small style={{fontSize: '12px', color: '#666'}}>
                HT: {calculateHT(totalCancelledAmountTTC).toFixed(2)} DT
              </small>
            </p>
          </div>
        </div>
        
        <div className="summary-card net">
          <div className="card-icon">🏦</div>
          <div className="card-content">
            <h3>Net à encaisser</h3>
            <p className="amount">{netAmountTTC.toFixed(2)} DT TTC</p>
            <p className="count">
              {dailyBills.length} factures<br/>
              <small style={{fontSize: '12px', color: '#666'}}>
                HT: {netAmountHT.toFixed(2)} DT
              </small>
            </p>
          </div>
        </div>
      </div>

      {/* Statistiques de paiement - AFFICHER AVEC REMISE */}
      {Object.keys(paymentStats).length > 0 && (
        <div className="payment-stats-section">
          <h3>💳 Méthodes de Paiement</h3>
          <div className="payment-stats-grid">
            {Object.entries(paymentStats).map(([method, stats]) => (
              <div key={method} className="payment-stat-card">
                <div className="payment-method">{method}</div>
                <div className="payment-amount">{stats.amountTTC.toFixed(2)} DT TTC</div>
                <div className="payment-count">{stats.count} facture{stats.count !== 1 ? 's' : ''}</div>
                {stats.totalDiscountAmount > 0 && (
                  <div className="payment-discount" style={{fontSize: '12px', color: '#d32f2f'}}>
                    Remise: -{stats.totalDiscountAmount.toFixed(2)} DT
                  </div>
                )}
                {stats.isStaffAdditionCount > 0 && (
                  <div className="payment-staff" style={{fontSize: '11px', color: '#ff9800', marginTop: '3px'}}>
                    👥 {stats.isStaffAdditionCount} addition{stats.isStaffAdditionCount !== 1 ? 's' : ''} personnel
                  </div>
                )}
                {stats.combinedPaymentCount > 0 && (
                  <div className="payment-combined" style={{fontSize: '11px', color: '#9c27b0', marginTop: '3px'}}>
                    🔄 {stats.combinedPaymentCount} combiné{stats.combinedPaymentCount !== 1 ? 's' : ''}
                  </div>
                )}
                <div className="payment-ht" style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
                  HT: {stats.amountHT.toFixed(2)} DT
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chargement */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Chargement des données...</p>
        </div>
      )}

      {/* Ventes actives */}
      <div className="section active-sales">
        <div className="section-header">
          <h3 className="section-title">🟢 Ventes Actives ({activeItems.length})</h3>
          <span className="section-total">{totalActiveAmountTTC.toFixed(2)} DT TTC</span>
        </div>
        
        {activeItems.length > 0 ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Heure</th>
                  <th>Produit</th>
                  <th>Qté</th>
                  <th>Prix U. TTC</th>
                  <th>Prix Global</th>
                  <th>Remise</th>
                  <th>Paiement</th>
                  <th>Facture</th>
                  <th>Détail</th>
                </tr>
              </thead>
              <tbody>
                {activeItems.slice(0, 50).map(({ bill, item, details }, idx) => {
                  const isFirstItemForBill = activeItems.findIndex(a => a.bill._id === bill._id) === idx;
                  const unitPriceTTC = details.price;
                  const globalPrice = unitPriceTTC * (item.quantity || 1);
                  const originalGlobalPrice = details.originalPrice * (item.quantity || 1);
                  const discountAmount = originalGlobalPrice - globalPrice;
                  
                  return (
                    <tr key={`active-${bill._id}-${idx}`} className={details.hasDiscount ? "has-discount" : ""}>
                      <td>{idx + 1}</td>
                      <td>{new Date(bill.date).toLocaleTimeString("fr-FR", {hour: '2-digit', minute:'2-digit'})}</td>
                      <td>
                        {details.name || details.parent || "-"}
                        {details.isStaffAddition && (
                          <span style={{color: '#ff9800', marginLeft: '5px'}} title="Addition Personnel">👥</span>
                        )}
                        {bill.unifiedCombinedBill && (
                          <span style={{color: '#9c27b0', marginLeft: '5px'}} title="Paiement Combiné">🔄</span>
                        )}
                      </td>
                      <td>{item.quantity || 1}</td>
                      <td>
                        <div>{unitPriceTTC.toFixed(2)} DT TTC</div>
                        {details.originalPrice !== unitPriceTTC && (
                          <div style={{fontSize: '11px', color: '#666', textDecoration: 'line-through'}}>
                            {details.originalPrice.toFixed(2)} DT
                          </div>
                        )}
                        <div style={{fontSize: '11px', color: '#666'}}>
                          HT: {calculateHT(unitPriceTTC).toFixed(2)} DT
                        </div>
                      </td>
                      <td>
                        <div>{globalPrice.toFixed(2)} DT TTC</div>
                        {originalGlobalPrice !== globalPrice && (
                          <div style={{fontSize: '11px', color: '#666', textDecoration: 'line-through'}}>
                            {originalGlobalPrice.toFixed(2)} DT
                          </div>
                        )}
                        <div style={{fontSize: '11px', color: '#666'}}>
                          HT: {calculateHT(globalPrice).toFixed(2)} DT
                        </div>
                      </td>
                      <td>
                        {details.discountApplied > 0 ? (
                          <div style={{color: '#d32f2f', fontSize: '12px'}}>
                            -{details.discountApplied}%
                            <div style={{fontSize: '10px'}}>
                              -{discountAmount.toFixed(2)} DT
                            </div>
                          </div>
                        ) : (
                          <div style={{color: '#666', fontSize: '12px'}}>-</div>
                        )}
                      </td>
                      <td>{bill.paymentMethod || "-"}</td>
                      <td>
                        #{bill.billNumber || bill._id?.slice(-6)}
                        {bill.unifiedCombinedBill && (
                          <span style={{color: '#9c27b0', marginLeft: '5px'}}>🔄</span>
                        )}
                      </td>
                      <td>
                        {isFirstItemForBill && (
                          <button 
                            onClick={() => showBillDetails(bill)} 
                            className="view-bill-btn"
                            title="Voir détail"
                          >
                            👁️
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {activeItems.length > 50 && (
              <div className="table-footer">
                <p>... et {activeItems.length - 50} articles supplémentaires</p>
              </div>
            )}
          </div>
        ) : (
          <div className="no-data">
            <p>Aucune vente active aujourd'hui.</p>
          </div>
        )}
      </div>

      {/* Articles annulés */}
      {cancelledItems.length > 0 && (
        <div className="section cancelled-sales">
          <div className="section-header">
            <h3 className="section-title">🔴 Articles Annulés ({cancelledItems.length})</h3>
            <span className="section-total">{totalCancelledAmountTTC.toFixed(2)} DT TTC</span>
          </div>
          
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Heure</th>
                  <th>Produit</th>
                  <th>Qté</th>
                  <th>Prix U. TTC</th>
                  <th>Prix Global</th>
                  <th>Motif</th>
                  <th>Annulé à</th>
                  <th>Détail</th>
                </tr>
              </thead>
              <tbody>
                {cancelledItems.slice(0, 20).map(({ bill, item, details }, idx) => {
                  const isFirstItemForBill = cancelledItems.findIndex(c => c.bill._id === bill._id) === idx;
                  const unitPriceTTC = details.price;
                  const globalPrice = unitPriceTTC * (item.quantity || 1);
                  const itemHT = calculateHT(globalPrice);
                  
                  return (
                    <tr key={`cancelled-${bill._id}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>{new Date(bill.date).toLocaleTimeString("fr-FR", {hour: '2-digit', minute:'2-digit'})}</td>
                      <td>{details.name || details.parent || "-"}</td>
                      <td>{item.quantity || 1}</td>
                      <td>
                        <div>{unitPriceTTC.toFixed(2)} DT TTC</div>
                        <div style={{fontSize: '11px', color: '#666'}}>
                          HT: {calculateHT(unitPriceTTC).toFixed(2)} DT
                        </div>
                      </td>
                      <td>
                        <div>{globalPrice.toFixed(2)} DT TTC</div>
                        <div style={{fontSize: '11px', color: '#666'}}>
                          HT: {itemHT.toFixed(2)} DT
                        </div>
                      </td>
                      <td className="cancel-reason">{item.cancelReason || "Non spécifié"}</td>
                      <td>{item.cancelledAt ? new Date(item.cancelledAt).toLocaleTimeString("fr-FR") : "-"}</td>
                      <td>
                        {isFirstItemForBill && (
                          <button 
                            onClick={() => showBillDetails(bill)} 
                            className="view-bill-btn"
                            title="Voir détail"
                          >
                            👁️
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Produits par catégorie - AFFICHER AVEC REMISE */}
      {Object.keys(productSalesByCategory).length > 0 && (
        <div className="product-summary-section">
          <h3>📦 Produits Vendus</h3>
          {Object.entries(productSalesByCategory).map(([category, data]) => (
            data.totalQuantity > 0 && (
              <div key={category} className="category-card">
                <div className="category-header">
                  <h4>{category}</h4>
                  <span className="category-total">
                    {data.totalTTC.toFixed(2)} DT TTC<br/>
                    {data.totalDiscountAmount > 0 && (
                      <small style={{fontSize: '11px', color: '#d32f2f'}}>
                        Remise: -{data.totalDiscountAmount.toFixed(2)} DT
                      </small>
                    )}
                    {data.staffAdditionCount > 0 && (
                      <small style={{fontSize: '11px', color: '#ff9800', display: 'block'}}>
                        👥 {data.staffAdditionCount} addition{data.staffAdditionCount !== 1 ? 's' : ''} personnel
                      </small>
                    )}
                    {data.combinedPaymentCount > 0 && (
                      <small style={{fontSize: '11px', color: '#9c27b0', display: 'block'}}>
                        🔄 {data.combinedPaymentCount} paiement{data.combinedPaymentCount !== 1 ? 's' : ''} combiné
                      </small>
                    )}
                    <small style={{fontSize: '12px'}}>
                      HT: {data.totalHT.toFixed(2)} DT
                    </small>
                  </span>
                </div>
                <div className="category-products">
                  {Object.values(data.products).map((product, index) => (
                    <div key={index} className="product-item">
                      <span className="product-name">
                        {product.name}
                        {product.isStaffAddition && (
                          <span style={{color: '#ff9800', marginLeft: '5px'}} title="Addition Personnel">👥</span>
                        )}
                        {product.isCombinedPayment && (
                          <span style={{color: '#9c27b0', marginLeft: '5px'}} title="Paiement Combiné">🔄</span>
                        )}
                      </span>
                      <span className="product-quantity">x{product.quantity}</span>
                      <span className="product-total">
                        {product.totalTTC.toFixed(2)} DT TTC
                        {product.totalDiscount > 0 && (
                          <div style={{fontSize: '10px', color: '#d32f2f'}}>
                            -{product.totalDiscount.toFixed(2)} DT
                            {product.isStaffAddition && (
                              <span style={{color: '#ff9800'}}> (Personnel)</span>
                            )}
                            {product.isCombinedPayment && (
                              <span style={{color: '#9c27b0'}}> (Combiné)</span>
                            )}
                          </div>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Section dédiée aux additions personnels */}
      {staffAdditionDetails.count > 0 && (
        <div className="staff-section">
          <h3>👥 Additions Personnel ({staffAdditionDetails.count})</h3>
          <div className="staff-stats">
            <div className="staff-summary">
              <div className="staff-summary-item">
                <span className="staff-label">Total additions:</span>
                <span className="staff-value">{staffAdditionDetails.count}</span>
              </div>
              <div className="staff-summary-item">
                <span className="staff-label">Montant total:</span>
                <span className="staff-value">{staffAdditionDetails.totalAmount.toFixed(2)} DT TTC</span>
              </div>
            </div>
            
            {Array.from(staffAdditionDetails.staffMembers.values()).length > 0 && (
              <div className="staff-members-list">
                <h4>Détails par employé:</h4>
                <div className="staff-members-grid">
                  {Array.from(staffAdditionDetails.staffMembers.values()).map((staff, index) => (
                    <div key={index} className="staff-member-card">
                      <div className="staff-member-header">
                        <span className="staff-member-name">{staff.name}</span>
                        <span className="staff-member-matricule">
                          ({staff.matricule && staff.matricule !== "N/A" ? staff.matricule : "Sans matricule"})
                        </span>
                      </div>
                      <div className="staff-member-details">
                        <div className="staff-member-stat">
                          <span>Nombre d'additions:</span>
                          <span className="staff-member-count">{staff.count}</span>
                        </div>
                        <div className="staff-member-stat">
                          <span>Montant total:</span>
                          <span className="staff-member-amount">{staff.totalAmount.toFixed(2)} DT TTC</span>
                        </div>
                        {staff.department && (
                          <div className="staff-member-stat">
                            <span>Département:</span>
                            <span className="staff-member-department">{staff.department}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketCaisseS;